/**
 * Queue Restore API
 *
 * API endpoints for queue restore operations
 */

import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type { GetQueueRestores, SaveQueueRestore, SaveQueueRestoreUpdate } from "./queueRestore.types";

/**
 * Queue Restore API endpoints
 */
export const queueRestoreApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all queue restore entries
     */
    getQueueRestores: build.query<GetQueueRestores["result"], GetQueueRestores["args"]>({
      query: () => `/queue-restore`
    }),

    /**
     * Restore the queue by company and queue ids
     */
    saveQueueRestore: build.mutation<SaveQueueRestore["result"], SaveQueueRestore["args"]>({
      query: (body) => ({
        url: "/queue-restore/restore",
        method: "PUT",
        body
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Realidando resgate...",
          success: "Resgate realizado com sucesso!",
          error: "Erro ao efetuar o resgate!"
        });
      },
      extraOptions: {
        maxRetries: 3
      }
    }),

    /**
     * Update a queue restore entry by ID
     */
    saveQueueRestoreUpdate: build.mutation<SaveQueueRestoreUpdate["result"], SaveQueueRestoreUpdate["args"]>({
      query: ({ qtyTransported, ...body }) => ({
        url: "/queue-restore/update",
        method: "PUT",
        body: {
          qtyTransported: Number(qtyTransported),
          ...body
        }
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Atualizando resgate...",
          success: "Resgate atualizado com sucesso!",
          error: (rawErr) => {
            const err = rawErr as { error: { statusCode: number; data: { message: string; error: string } } } | undefined;

            return err?.error?.data?.message ?? "Erro ao atualizar o resgate!";
          }
        });
      }
    }),

    /**
     * Delete a queue restore entry by ID
     */
    saveQueueRestoreDelete: build.mutation<void, number>({
      query: (id) => ({
        url: `/queue-restore/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo resgate...",
          success: "Resgate excluído com sucesso!",
          error: "Erro ao excluir o resgate!"
        });
      }
    })
  })
});

// Enhance endpoints with tag types
queueRestoreApi.enhanceEndpoints({
  addTagTypes: ["QueueRestore"],
  endpoints: {
    getQueueRestores: {
      providesTags: (result) => {
        if (!result) return [{ type: "QueueRestore", id: "LIST" }];

        return [
          ...result.map(({ id }) => ({
            type: "QueueRestore" as const,
            id
          })),
          { type: "QueueRestore", id: "LIST" }
        ];
      }
    },
    saveQueueRestore: {
      invalidatesTags: ["QueueRestore"]
    },
    saveQueueRestoreDelete: {
      invalidatesTags: ["QueueRestore"]
    }
  }
});
